import { msalInstance } from 'src/auth/msalInstance';
import { loginRequest } from 'src/auth/authConfig';
import { getErrorMessage, logger, showErrorToast } from 'src/utils';
import { getStatusCode } from './errorClassification';

/**
 * Per-query/mutation side-effect declarations, read by the centralized error
 * pipeline and the mutation success handler (queryClient.ts).
 */
export interface MutationMeta {
  successMessage?: string;
  successMessageFn?: (data: unknown) => string;
  /** Component owns the error UI; the global pipeline should not toast. */
  suppressErrorToast?: boolean;
  /** Background sync — no toasts at all (still logged). */
  silent?: boolean;
  /** Statuses this query/mutation expects as valid outcomes (e.g. an
   * optional lookup that 404s) — suppresses the toast and downgrades the
   * log to debug. */
  expectedStatuses?: number[];
  /** Free-text context appended to the log line for the default branch. */
  errorContext?: string;
}

/**
 * Single global error handler wired into `QueryCache.onError` /
 * `MutationCache.onError` (queryClient.ts). Collapses the app's ~207
 * duplicated `try/catch + showErrorToast + logger.error` blocks into one
 * status-aware classifier.
 */
export function handleApiError(
  err: unknown,
  meta: MutationMeta | undefined,
  kind: 'query' | 'mutation',
): void {
  const status = getStatusCode(err);
  const m = meta ?? {};

  if (status && m.expectedStatuses?.includes(status)) {
    logger.debug(`expected ${status}`, { err });
    return;
  }

  switch (status) {
    case 401:
      logger.warn('401 — re-auth', { err });
      msalInstance.acquireTokenRedirect(loginRequest);
      return;
    case 403:
      logger.warn('403', { err });
      if (!m.silent && !m.suppressErrorToast) {
        showErrorToast("You don't have access to this resource.");
      }
      return;
    default:
      logger.error(`request failed (${m.errorContext ?? kind})`, { err });
      if (!m.silent && !m.suppressErrorToast) {
        showErrorToast(getErrorMessage(err));
      }
  }
}
